import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { CalendarToday } from "@mui/icons-material";

const Update_PaymentStudent = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { id } = useParams(); // This is now the payment ID
  const navigate = useNavigate();
  const location = useLocation();

  // Get payment data and student ID from navigation state
  const { paymentData, studentId } = location.state || {};

  const [studentData, setStudentData] = React.useState({
    name: "",
    phone_number: "",
    tuition_fee: 0,
    discount_amount: 0,
    branch: { name: "{}" },
    class_model: { name: "{}" },
    section: { name: "{}" },
    tuitions: [],
  });

  // Initialize form fields with passed payment data or defaults
  const [amount_paid, setAmount_paid] = React.useState(
    paymentData?.amount_paid || 0
  );
  const [date, setDate] = React.useState(
    paymentData?.payment_date || new Date().toLocaleDateString("en-CA")
  );

  const token = localStorage.getItem("token");
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const parseJsonField = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return parsed[i18n.language] || parsed.en || "N/A";
    } catch (e) {
      return "N/A";
    }
  };

  React.useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://api.clever-school.com/api/students/${studentId}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (isMounted) {
          const userData = response.data;
          setStudentData({
            ...userData,
            tuition_fee: userData.tuitions?.[0]?.tuition_fee || 0,
            discount_amount: userData.tuitions?.[0]?.discount_amount || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        message.error(t("error.fetchData"));
      }
    };

    if (studentId) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [studentId, t, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount_paid: amount_paid,
        payment_date: date,
      };

      // Use PUT for update, POST for create (though this component is only for updates)
      await axios.put(
        `https://api.clever-school.com/api/payments/${id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("paymentUpdated"));
      navigate("/GetStudent");
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error(t("paymentUpdateFailed"));
    }
  };

  const totalAmountPaid =
    studentData.tuition_payments?.reduce(
      (sum, payment) => sum + parseFloat(payment.amount_paid || 0),
      0
    ) || 0;

  const remainingBalance =
    parseFloat(studentData.tuitions[0]?.final_amount || 0) - totalAmountPaid;

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Student Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("studentInformation")}
          </Typography>

          <Grid container spacing={3}>
            {[
              { label: t("name"), value: studentData.name, icon: null },
              {
                label: t("branch"),
                value: parseJsonField(studentData.branch.name),
                icon: null,
              },
              {
                label: t("tuitionFee"),
                value: parseFloat(studentData.tuition_fee).toLocaleString(
                  "en-US"
                ),
              },
              {
                label: t("discountAmount"),
                value: parseFloat(studentData.discount_amount).toLocaleString(
                  "en-US"
                ),
              },
              {
                label: t("totalPaid"),
                value: totalAmountPaid.toLocaleString("en-US"),
                color: "success.main",
              },
              {
                label: t("remainingBalance"),
                value: Math.abs(remainingBalance).toLocaleString("en-US"),
                color: remainingBalance > 0 ? "error.main" : "success.main",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {item.icon}
                      {item.label}
                    </Typography>
                    <Typography variant="h6" sx={{ color: item.color }}>
                      {item.label.includes("amount") ||
                      item.label.includes("Fee")
                        ? `${item.value}`
                        : item.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Payment Form Section */}
        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 500,
              mb: 4,
              color: theme.palette.secondary.main,
            }}
          >
            {t("updatePayment")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("amountPaid")}
                type="number"
                value={amount_paid}
                onChange={(e) => setAmount_paid(parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ mr: 1 }}
                    >
                      IQD
                    </Typography>
                  ),
                }}
                variant="outlined"
                helperText={t("enterPaymentAmount")}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("paymentDate")}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarToday
                      sx={{ mr: 1, color: theme.palette.text.secondary }}
                    />
                  ),
                }}
                variant="outlined"
                helperText={t("selectPaymentDate")}
                required
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/GetStudent")}
              sx={{
                px: 4,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {t("update")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Update_PaymentStudent;
